<template>
  <Container
    v-bind="containerProps"
    class="relative w-full"
    :base-class="[
      'w-full relative flex items-center h-9 text-gray-800 text-sm relative dark:text-gray-100',
      disabled ? 'opacity-75 pointer-events-none' : ''
    ]"
  >
    <o-dropdown
      ref="dropdown"
      class="w-full"
      :arrow="false"
      placement="bottom-start"
      container-class=""
      detect-overflow
      default-class="z-10 origin-top-right min-w-120 bg-white dark:bg-gray-900 rounded dark:border dark:border-gray-800"
      :disabled="disabled"
      @show="$emit('show')"
    >
      <div class="w-full h-9 relative flex items-center justify-between text-sm text-gray-800 cursor-pointer pr-4 px-2 dark:text-gray-100">
        <slot v-if="!isEmpty" name="value" :value="_value">
          <span class="truncate flex-shrink max-w-[120px]">
            {{ valueLabel }}
          </span>
        </slot>

        <p v-if="isEmpty" class="text-sm text-gray-500 dark:text-gray-400">
          {{ placeholder }}
        </p>

        <div class="absolute inset-y-0 right-0 flex items-center px-2 text-gray-600">
          <o-icon icon="accordion" :size="12" />
        </div>
      </div>

      <template #content="{ overflow }">
        <div
          class="text-sm flex flex-col w-120 overflow-x-hidden"
          :style="{
            height: overflow && overflow.bottom > 0 ? `${400 - overflow.bottom}px` : '400px'
          }"
        >
          <div v-if="search" class="w-full flex items-center border-b">
            <o-icon icon="search" class="text-gray-500 dark:text-gray-400 ml-4" :size="14" />
            <input v-model="query" type="text" :placeholder="$t('general.search')" class="py-2 px-3 w-full dark:bg-gray-900">
          </div>

          <slot name="content">
            <o-scroll v-if="filtered">
              <ul
                v-for="(category, indexCategory) in filtered"
                :key="indexCategory"
              >
                <li v-if="category.label" class="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mt-3 mb-1 px-4">
                  {{ category.label }}
                </li>
                <li
                  v-for="(option, indexOption) in category.options"
                  :key="indexOption"
                  class="text-gray-800 cursor-pointer select-none relative py-2 px-3 transition-colors dark:text-gray-100"
                  :class="[
                    isOptionSelected(option.value) ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-800/20 dark:text-blue-300 dark:hover:bg-blue-900/50' : 'hover:bg-gray-100 dark:hover:bg-gray-800',
                    option.class
                  ]"
                  role="option"
                  @click="handleOptionClick(option.value)"
                >
                  <slot name="option" :option="option" :query="query">
                    <span class="w-full flex items-center justify-between">
                      {{ option.label }}

                      <o-icon
                        v-if="isOptionSelected(option.value)"
                        icon="check"
                        :size="9"
                        class="text-blue-600 dark:text-blue-400"
                        fill
                      />
                    </span>
                  </slot>
                </li>
              </ul>
            </o-scroll>

            <p v-else class="text-center p-4 text-gray-600 dark:text-gray-300">
              {{ $t('components.fields.listBox.noOptions') }}
            </p>
          </slot>

          <div v-if="clear && !isEmpty" class="w-full border-t pt-1 px-1">
            <ul>
              <li
                class="text-gray-800 cursor-pointer select-none relative py-1 transition-colors hover:bg-gray-100 w-full block text-center rounded"
                role="option"
                @click="handleClear"
              >
                <span class="w-full flex items-center justify-center">
                  {{ $t('components.fields.listBox.clearSelected') }}

                  <o-icon
                    icon="x"
                    :size="12"
                    class="text-blue-600 ml-2"
                    fill
                  />
                </span>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </o-dropdown>

    <template #leading>
      <slot name="leading" />
    </template>

    <template #trailing>
      <slot name="trailing" />
    </template>
  </Container>
</template>

<script setup lang="ts">
import Fuse from 'fuse.js'
import get from 'lodash/get'

import Container from './Container.vue'

const { $i18n } = useNuxtApp()

interface Props {
  search?: boolean
  categorised?: boolean
  modelValue?: any[] | Record<string, any> | string | number
  required?: boolean
  placeholder?: string
  clear?: boolean
  searchKeys?: string[]
  // multi mixin
  valueAttribute?: string
  textAttribute?: string
  options?: any[] | Record<string, any>
  // input mixin
  id?: string | number | null
  name?: string | null
  readonly?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  leading?: string | string[] | null
  trailing?: string | string[] | null
  loading?: boolean
  baseClass?: string | Record<string, any> | string[]
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  status?: boolean | string | null
  autofocus?: boolean | null
  tabindex?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  search: false,
  categorised: false,
  modelValue: () => [],
  required: false,
  placeholder: undefined,
  clear: false,
  searchKeys: () => ['text', 'info', 'value', 'label'],
  valueAttribute: undefined,
  textAttribute: undefined,
  options: () => [],
  id: null,
  name: null,
  readonly: false,
  disabled: false,
  size: null,
  pattern: null,
  leading: null,
  trailing: null,
  loading: false,
  baseClass: 'h-9 w-full relative flex items-center',
  border: 'border dark:border-gray-800',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  defaultSizeClass: 'h-9',
  largeSizeClass: 'text-lg h-12',
  smallSizeClass: 'text-sm h-8',
  status: null,
  autofocus: null,
  tabindex: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'show'): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

const dropdown = ref<any>()
const query = ref('')
const valueWhenFocus = ref<any>(null)

const _value = computed({
  get () {
    return props.modelValue
  },
  set (value: any) {
    emit('update:modelValue', value)
  },
})

function isOptionSelected (optionValue: any) {
  return Array.isArray(_value.value)
    ? _value.value.includes(optionValue)
    : optionValue === _value.value
}

// -- multi mixin --
function guessOptionValue (option: any) {
  if (props.valueAttribute) return get(option, props.valueAttribute)
  return get(option, 'value', get(option, 'id', get(option, 'text')))
}

function guessOptionText (option: any) {
  if (props.textAttribute) return get(option, props.textAttribute)
  return get(option, 'text', get(option, 'label'))
}

function normalizeOption (option: any): any {
  if (['string', 'number', 'boolean'].includes(typeof option)) {
    return { value: option, text: option }
  }
  return {
    value: guessOptionValue(option),
    text: guessOptionText(option),
  }
}

const normalizedOptions = computed(() => {
  if (props.options) {
    if (Array.isArray(props.options)) {
      return props.options.map((option: any) => normalizeOption(option))
    } else {
      return Object.keys(props.options).map(key => ({
        value: key,
        text: (props.options as Record<string, any>)[key],
      }))
    }
  }
  return []
})

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const containerProps = computed(() => {
  const classes = [...currentClass.value]
  if (props.border) classes.push(props.border as string)
  if (props.rounded) classes.push(props.rounded as string)
  if (props.shadow) classes.push(props.shadow as string)
  if (props.highlight) classes.push(props.highlight as string)
  return {
    class: classes,
    baseClass: props.baseClass,
    leading: props.leading,
    trailing: props.trailing,
    loading: props.loading,
  }
})

const filtered = computed(() => {
  let items: any = props.options

  if (query.value.length) {
    if (props.categorised) {
      const arrays = Object.values(items).map(({ options }: any) => options)
      items = ([] as any[]).concat(...arrays)
    }

    const fuse = new Fuse(items, {
      keys: props.searchKeys,
      distance: 200,
    })
    const results = fuse.search(query.value)
    items = results.map(({ item }) => item)
  }

  if (!props.categorised || query.value.length) {
    return [{ options: items }]
  }
  return items
})

const isMulti = computed(() => {
  return props.modelValue && Array.isArray(props.modelValue)
})

const isEmpty = computed(() => {
  if (isMulti.value) {
    return (props.modelValue as any[])?.length === 0
  }
  return props.modelValue === null || props.modelValue === undefined
})

const valueLabel = computed(() => {
  if (isMulti.value) {
    return (_value.value as any[])
      .map((item: any) => item ? item.name || item.title || item.displayName : ($i18n as any).t('general.none'))
      .join(', ')
  }

  const val = _value.value as any
  return val?.name || val?.title || (props.modelValue as any)?.displayName
})

function handleClear () {
  _value.value = isMulti.value ? [] : null
}

function handleOptionClick (value: any) {
  if (isMulti.value) {
    const arr = _value.value as any[]
    if (!arr.includes(value)) {
      arr.push(value)
    } else {
      const i = arr.indexOf(value)
      if (i >= 0) {
        arr.splice(i, 1)
      }
    }
  } else {
    _value.value = value
    query.value = ''
    dropdown.value?.close()
  }
}

function onBlur (e: Event) {
  emit('blur', e)
}

function onFocus (e: Event) {
  emit('focus', e)
}

function onKeyUp (e: Event) {
  emit('keyup', e)
}

function onKeyDown (e: Event) {
  emit('keydown', e)
}
</script>
